import useMutationApi from '../useMutationApi';
import useGetApi from '../useGetApi';
import { useQueryClient } from '@tanstack/react-query';

export interface ShareableLink {
  id: string;
  token: string;
  name: string | null;
  resourceType: string;
  metadata: Record<string, unknown>;
  allowedEmails: string[];
  createdByUserId: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateShareableLinkInput {
  name?: string | null;
  resourceType: string;
  metadata?: Record<string, unknown>;
  allowedEmails: string[]; // At least one email required
  expiresAt?: string | null;
}

interface UpdateShareableLinkInput {
  name?: string | null;
  metadata?: Record<string, unknown>;
  allowedEmails?: string[];
  expiresAt?: string | null;
}

interface ShareableLinkResponse {
  shareableLink: ShareableLink;
}

interface ShareableLinkByTokenResponse {
  shareableLink: ShareableLink;
  isCreator: boolean;
}

export function useCreateShareableLink() {
  const queryClient = useQueryClient();

  return useMutationApi<CreateShareableLinkInput, ShareableLinkResponse>({
    endpoint: 'core/shareable-links',
    method: 'POST',
    successMessage: 'Shareable link created',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['shareable-links'] });
      },
    },
  });
}

export function useUpdateShareableLink(linkId: string) {
  const queryClient = useQueryClient();

  return useMutationApi<UpdateShareableLinkInput, ShareableLinkResponse>({
    endpoint: `core/shareable-links/${linkId}`,
    method: 'PUT',
    successMessage: 'Shareable link updated',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['shareable-links'] });
      },
    },
  });
}

export function useDeleteShareableLink(linkId: string) {
  const queryClient = useQueryClient();

  return useMutationApi<void, void>({
    endpoint: `core/shareable-links/${linkId}`,
    method: 'DELETE',
    successMessage: 'Shareable link deleted',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['shareable-links'] });
      },
    },
  });
}

export function useShareableLinks(params?: {
  resourceType?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
  /** Admins only: include links created by every user, not just the caller. */
  all?: boolean;
}) {
  return useGetApi<ShareableLinksWithCreatorResponse>({
    endpoint: 'core/shareable-links',
    queryKey: ['shareable-links', params],
    queryParameters: {
      resourceType: params?.resourceType,
      limit: params?.limit,
      offset: params?.offset,
      all: params?.all,
    },
    options: {
      enabled: params?.enabled ?? true,
    },
  });
}

export function useShareableLinkByToken(token: string, enabled = true) {
  return useGetApi<ShareableLinkByTokenResponse>({
    endpoint: `core/shareable-links/by-token/${token}`,
    queryKey: ['shareable-link-by-token', token],
    options: {
      enabled: enabled && !!token,
    },
  });
}

// Extended ShareableLink type that includes creator info
export interface ShareableLinkWithCreator extends ShareableLink {
  createdByUser?: {
    id: string;
    name: string;
  };
}

interface ShareableLinksWithCreatorResponse {
  shareableLinks: ShareableLinkWithCreator[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * Get links that have been shared with the current user
 */
export function useSharedWithMe(params?: {
  resourceType?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  return useGetApi<ShareableLinksWithCreatorResponse>({
    endpoint: 'core/shareable-links/shared-with-me',
    queryKey: ['shareable-links-shared-with-me', params],
    queryParameters: {
      resourceType: params?.resourceType,
      limit: params?.limit,
      offset: params?.offset,
    },
    options: {
      enabled: params?.enabled ?? true,
    },
  });
}

/**
 * Get links created by the current user (alias for useShareableLinks with creator info)
 */
export function useMySharedLinks(params?: {
  resourceType?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  return useGetApi<ShareableLinksWithCreatorResponse>({
    endpoint: 'core/shareable-links',
    queryKey: ['shareable-links', params],
    queryParameters: {
      resourceType: params?.resourceType,
      limit: params?.limit,
      offset: params?.offset,
    },
    options: {
      enabled: params?.enabled ?? true,
    },
  });
}

export function getShareableLinkUrl(token: string): string {
  return `${window.location.origin}/shared/${token}`;
}
